import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import {
  Wallet, ArrowUpRight, ArrowDownLeft, Clock, Shield,
  TrendingUp, IndianRupee, Download, ChevronDown, ChevronUp
} from "lucide-react";
import { format } from "date-fns";

const typeIcons: Record<string, any> = {
  deposit: ArrowDownLeft, commitment: Shield, payment: ArrowUpRight,
  refund: ArrowDownLeft, penalty: ArrowUpRight, settlement: ArrowUpRight,
};
const typeColors: Record<string, string> = {
  deposit: "text-green-400", commitment: "text-primary", payment: "text-destructive",
  refund: "text-green-400", penalty: "text-destructive", settlement: "text-secondary",
};

const WalletPage = () => {
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<any[]>([]);
  const [krewNames, setKrewNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [groupByKrew, setGroupByKrew] = useState(false);

  useEffect(() => {
    if (!user) return;
    const fetchData = async () => {
      const { data } = await supabase.from("transactions").select("*").eq("user_id", user.id).order("created_at", { ascending: false });
      if (data) {
        setTransactions(data);
        // Fetch krew names
        const krewIds = [...new Set(data.filter(t => t.krew_id).map(t => t.krew_id))];
        if (krewIds.length > 0) {
          const { data: krews } = await supabase.from("krews").select("id, title").in("id", krewIds);
          if (krews) {
            const map: Record<string, string> = {};
            krews.forEach(k => { map[k.id] = k.title; });
            setKrewNames(map);
          }
        }
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const totalSpent = transactions.filter((t) => ["payment", "settlement", "penalty"].includes(t.type) && t.status === "completed").reduce((s, t) => s + t.amount, 0);
  const totalRefunded = transactions.filter((t) => t.type === "refund" && t.status === "completed").reduce((s, t) => s + t.amount, 0);
  const activeCommitments = transactions.filter((t) => t.type === "commitment" && t.status === "completed").reduce((s, t) => s + t.amount, 0);
  const netBalance = totalRefunded - totalSpent;

  const exportCSV = () => {
    const header = "Date,Type,Amount,Description,Status\n";
    const rows = transactions.map(t => `${format(new Date(t.created_at), "yyyy-MM-dd")},${t.type},${t.amount},"${t.description || ""}",${t.status}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "krew-transactions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const groupedByKrew = groupByKrew ? transactions.reduce((acc: Record<string, any[]>, t) => {
    const key = t.krew_id || "other";
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {}) : null;

  const renderTransaction = (tx: any, i: number) => {
    const Icon = typeIcons[tx.type] || Clock;
    const isIncoming = ["deposit", "refund"].includes(tx.type);
    return (
      <motion.div key={tx.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
        className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${isIncoming ? "bg-green-500/10" : "bg-destructive/10"}`}>
          <Icon className={`h-5 w-5 ${typeColors[tx.type] || "text-muted-foreground"}`} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-foreground capitalize">{tx.type}</p>
          <p className="text-xs text-muted-foreground">{tx.description || "Transaction"}</p>
        </div>
        <div className="text-right">
          <p className={`text-sm font-bold ${isIncoming ? "text-green-400" : "text-foreground"}`}>{isIncoming ? "+" : "-"}₹{tx.amount.toLocaleString()}</p>
          <p className="text-xs text-muted-foreground">{format(new Date(tx.created_at), "MMM d")}</p>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="mx-auto max-w-2xl space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground"><span className="text-gradient-gold">Wallet</span></h1>
          <p className="mt-1 text-muted-foreground font-body">Track your nightlife finances</p>
        </div>
        {transactions.length > 0 && (
          <button onClick={exportCSV} className="flex items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm text-muted-foreground hover:text-foreground">
            <Download className="h-4 w-4" /> Export CSV
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {[
          { icon: IndianRupee, value: `₹${Math.abs(netBalance).toLocaleString()}`, label: netBalance >= 0 ? "Net Positive" : "Net Spent", color: netBalance >= 0 ? "text-green-400" : "text-destructive" },
          { icon: Shield, value: `₹${activeCommitments.toLocaleString()}`, label: "Active Deposits", color: "text-primary" },
          { icon: TrendingUp, value: `₹${totalRefunded.toLocaleString()}`, label: "Refunded", color: "text-green-400" },
          { icon: ArrowUpRight, value: `₹${totalSpent.toLocaleString()}`, label: "Total Spent", color: "text-secondary" },
        ].map((card, i) => (
          <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
            className="rounded-2xl border border-border bg-card p-5">
            <card.icon className={`mb-2 h-5 w-5 ${card.color}`} />
            <p className={`text-xl font-bold ${card.color}`}>{card.value}</p>
            <p className="text-xs text-muted-foreground">{card.label}</p>
          </motion.div>
        ))}
      </div>

      {/* Transactions */}
      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-foreground">Transaction History</h2>
          <button onClick={() => setGroupByKrew(!groupByKrew)} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
            {groupByKrew ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {groupByKrew ? "Timeline" : "Group by Krew"}
          </button>
        </div>
        {loading ? (
          <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-card" />)}</div>
        ) : transactions.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-12 text-center">
            <Wallet className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
            <h3 className="mb-1 text-lg font-semibold text-foreground">No transactions yet</h3>
            <p className="text-sm text-muted-foreground">Your financial history will appear here</p>
          </div>
        ) : groupedByKrew ? (
          <div className="space-y-6">
            {Object.entries(groupedByKrew).map(([krewId, txs]) => (
              <div key={krewId}>
                <h3 className="mb-2 text-sm font-semibold text-foreground">{krewNames[krewId] || "Other"}</h3>
                <div className="space-y-2">{(txs as any[]).map((tx, i) => renderTransaction(tx, i))}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">{transactions.map((tx, i) => renderTransaction(tx, i))}</div>
        )}
      </section>
    </div>
  );
};

export default WalletPage;
